import React from "react";
import { Icon, notify } from "../registry";
import { NumInput, TextField, Seg } from "./fields";
import { CADENCES, apRowsFromRecords, mergeQboBills, monthlyEquivalent } from "../lib/cashplan";
import { csvRecords, toCsv, downloadCsv } from "../lib/csv";
/* ============================================================
   Cash forecast → the owner's money-out schedules (settings.cash):
     bills      one-off: { id, vendor, amount, due, paid, qboId?, source? }
     recurring  { id, name, amount, cadence, next, category }
     payroll    { id, name, amount, cadence, next }   (a run → PAYROLL WEEK)
     debt       { id, lender, payment, cadence, next, balance? }
   plus the CSV upload for bills / recurring and the QuickBooks pull (bank
   balance + open bills, read-only). Everything here is typed in or
   imported; the pipeline side of the forecast is computed in business.tsx.
   ============================================================ */
const CS_money0 = (n: number) => window.VFP.money0(n);
const CS_money2 = (n: number) => "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const CATS = ["Rent", "Insurance", "Truck / equipment", "Software", "Utilities", "Phone / internet", "Office", "Marketing", "Other"];
const newRowId = (p: string) => p + "-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
const todayYmd = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); };

/* field layout per schedule; `k` is the stored key */
const KINDS: any = {
  bills: { noun: "bill", fields: [
    { k: "vendor", t: "text", ph: "Vendor", grow: true },
    { k: "amount", t: "money", ph: "Amount" },
    { k: "due", t: "date", label: "Due" },
  ] },
  recurring: { noun: "recurring cost", fields: [
    { k: "name", t: "text", ph: "What it is (e.g. Shop rent)", grow: true },
    { k: "amount", t: "money", ph: "Amount" },
    { k: "cadence", t: "cadence" },
    { k: "next", t: "date", label: "Next" },
    { k: "category", t: "cat" },
  ] },
  payroll: { noun: "payroll run", fields: [
    { k: "name", t: "text", ph: "Crew payroll", grow: true },
    { k: "amount", t: "money", ph: "Per run" },
    { k: "cadence", t: "cadence" },
    { k: "next", t: "date", label: "Next pay date" },
  ] },
  debt: { noun: "loan payment", fields: [
    { k: "lender", t: "text", ph: "Lender / what it's for", grow: true },
    { k: "payment", t: "money", ph: "Payment" },
    { k: "cadence", t: "cadence" },
    { k: "next", t: "date", label: "Next" },
    { k: "balance", t: "money", ph: "Balance (optional)", optional: true },
  ] },
};
const blankOf = (kind: string) => {
  const r: any = {};
  KINDS[kind].fields.forEach((f: any) => { r[f.k] = f.t === "cadence" ? (kind === "payroll" ? "biweekly" : "monthly") : f.t === "cat" ? "Other" : f.t === "date" ? todayYmd() : f.t === "money" ? null : ""; });
  return r;
};

function Field({ f, value, onCommit, isNew }: any) {
  const lab = (s: string) => <span className="faint" style={{ fontSize: 11, marginRight: 4 }}>{s}</span>;
  if (f.t === "text") return (
    <div className="bz-grow" style={{ flex: "1 1 170px", minWidth: 140 }}>
      {isNew
        ? <input className="input" value={value || ""} placeholder={f.ph} aria-label={f.ph} onChange={e => onCommit(e.target.value)} />
        : <TextField value={value} placeholder={f.ph} onCommit={onCommit} />}
    </div>
  );
  if (f.t === "money") return <NumInput value={value} prefix="$" step={50} width={78} placeholder={f.optional ? "—" : "0"} label={f.ph} onCommit={(v: any) => onCommit(v)} />;
  if (f.t === "date") return (
    <label className="row" style={{ gap: 2, flexShrink: 0 }}>{f.label && lab(f.label)}
      <input className="input" type="date" value={value || ""} aria-label={f.label} style={{ width: 148, padding: "6px 8px" }} onChange={e => onCommit(e.target.value)} />
    </label>
  );
  if (f.t === "cadence") return (
    <select className="select" value={value || "monthly"} aria-label="How often" style={{ width: 148, padding: "6px 30px 6px 10px" }} onChange={e => onCommit(e.target.value)}>
      {CADENCES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
    </select>
  );
  if (f.t === "cat") return (
    <select className="select" value={value || "Other"} aria-label="Category" style={{ width: 150, padding: "6px 30px 6px 10px" }} onChange={e => onCommit(e.target.value)}>
      {[...CATS, ...(value && !CATS.includes(value) ? [value] : [])].map((c) => <option key={c} value={c}>{c}</option>)}
    </select>
  );
  return null;
}

function ScheduleList({ kind, rows, onChange }: any) {
  const def = KINDS[kind];
  const [draft, setDraft] = React.useState<any>(() => blankOf(kind));
  React.useEffect(() => { setDraft(blankOf(kind)); }, [kind]);
  const moneyKey = kind === "debt" ? "payment" : "amount";
  const nameKey = def.fields[0].k;
  const canAdd = String(draft[nameKey] || "").trim() && Number(draft[moneyKey]) > 0;
  const add = () => {
    if (!canAdd) return;
    onChange([...(rows || []), { id: newRowId(kind), ...draft, [nameKey]: String(draft[nameKey]).trim(), ...(kind === "bills" ? { paid: false, source: "manual" } : {}) }]);
    setDraft(blankOf(kind));
  };
  const upd = (id: string, patch: any) => onChange(rows.map((r: any) => (r.id === id ? { ...r, ...patch } : r)));
  const list = kind === "bills" ? (rows || []).slice().sort((a: any, b: any) => (a.paid ? 1 : 0) - (b.paid ? 1 : 0) || String(a.due || "").localeCompare(String(b.due || ""))) : (rows || []);
  const perMonth = kind === "bills" ? 0 : (rows || []).reduce((a: number, r: any) => a + monthlyEquivalent(r[moneyKey], r.cadence), 0);
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {list.length === 0 && <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>No {def.noun}s yet — add one below{kind === "bills" || kind === "recurring" ? ", or upload a CSV" : ""}.</p>}
      {list.map((r: any) => (
        <div key={r.id} className="ready-row" style={{ opacity: r.paid ? 0.55 : 1 }}>
          {def.fields.map((f: any) => <Field key={f.k} f={f} value={r[f.k]} onCommit={(v: any) => upd(r.id, { [f.k]: v })} />)}
          {kind === "bills" && (
            <label className="row" style={{ gap: 5, fontSize: 12, flexShrink: 0, cursor: "pointer" }}>
              <input type="checkbox" checked={!!r.paid} onChange={e => upd(r.id, { paid: e.target.checked })} />Paid
            </label>
          )}
          {r.source === "quickbooks" && <span className="chip" style={{ fontSize: 10.5, padding: "2px 8px" }}>QuickBooks</span>}
          {r.source === "csv" && <span className="chip" style={{ fontSize: 10.5, padding: "2px 8px" }}>CSV</span>}
          <button className="pe-del" title={"Delete this " + def.noun} aria-label={"Delete this " + def.noun} onClick={() => onChange(rows.filter((x: any) => x.id !== r.id))}><Icon name="trash" size={13} /></button>
        </div>
      ))}
      <div className="ready-row" style={{ borderStyle: "dashed" }}>
        {def.fields.map((f: any) => <Field key={f.k} f={f} isNew value={draft[f.k]} onCommit={(v: any) => setDraft((d: any) => ({ ...d, [f.k]: v }))} />)}
        <button className="btn btn-primary btn-sm" disabled={!canAdd} onClick={add}><Icon name="plus" size={13} />Add</button>
      </div>
      {perMonth > 0 && <p className="faint" style={{ fontSize: 11.5, margin: 0 }}>About {CS_money0(perMonth)} a month, all {def.noun}s together.</p>}
    </div>
  );
}

/* ---- AP upload: CSV → preview → add ---- */
const TEMPLATES: any = {
  bills: { cols: [{ key: "vendor", label: "Vendor" }, { key: "amount", label: "Amount" }, { key: "due", label: "Due date" }],
    rows: [{ vendor: "Menards", amount: 1840.5, due: "10/15/2026" }, { vendor: "Brown County Transfer", amount: 285, due: "10/3/2026" }] },
  recurring: { cols: [{ key: "name", label: "Name" }, { key: "amount", label: "Amount" }, { key: "cadence", label: "Cadence" }, { key: "next", label: "Next date" }, { key: "category", label: "Category" }],
    rows: [{ name: "Shop rent", amount: 2400, cadence: "monthly", next: "11/1/2026", category: "Rent" }, { name: "Truck insurance", amount: 1860, cadence: "quarterly", next: "12/15/2026", category: "Insurance" }] },
};
function CsvUpload({ cash, setC }: any) {
  const [kind, setKind] = React.useState<any>("bills");
  const [preview, setPreview] = React.useState<any>(null);
  const [fileName, setFileName] = React.useState<any>("");
  const read = (file: any) => {
    if (!file) return;
    setFileName(file.name || "file.csv");
    const fr = new FileReader();
    fr.onload = () => { try { setPreview(apRowsFromRecords(csvRecords(String(fr.result || "")), kind)); } catch (e) { setPreview({ missing: [], rows: [], error: "Couldn't read that file as CSV." }); } };
    fr.onerror = () => setPreview({ missing: [], rows: [], error: "Couldn't open that file." });
    fr.readAsText(file);
  };
  const good = preview ? preview.rows.filter((r: any) => r.ok) : [];
  // re-uploading the same export shouldn't stack duplicates: a bill matches on
  // vendor + amount + due date, a recurring cost on name + amount + cadence
  const existing = (cash[kind] || []) as any[];
  const sig = (x: any) => kind === "bills" ? [String(x.vendor || "").toLowerCase(), Number(x.amount), x.due].join("|") : [String(x.name || "").toLowerCase(), Number(x.amount), x.cadence].join("|");
  const have = new Set(existing.map(sig));
  const fresh = good.filter((r: any) => !have.has(sig(r.data)));
  const addAll = () => {
    const stamp = Date.now();
    const add = fresh.map((r: any, i: number) => ({ id: newRowId(kind) + i, ...r.data, ...(kind === "bills" ? { paid: false } : {}), source: "csv", addedAt: stamp }));
    setC({ [kind]: [...existing, ...add] });
    notify(`Added ${add.length} ${kind === "bills" ? "bill" : "recurring cost"}${add.length === 1 ? "" : "s"} from ${fileName}`);
    setPreview(null); setFileName("");
  };
  const t = TEMPLATES[kind];
  return (
    <div>
      <div className="row gap-8" style={{ flexWrap: "wrap", marginBottom: 10 }}>
        <Seg label="What the file holds" value={kind} onChange={(k: string) => { setKind(k); setPreview(null); setFileName(""); }}
          options={[{ id: "bills", label: "Bills (AP)" }, { id: "recurring", label: "Recurring costs" }]} />
        <label className="btn btn-outline btn-sm" style={{ cursor: "pointer" }}>
          <Icon name="clipboard" size={13} />Choose CSV…
          <input type="file" accept=".csv,.tsv,.txt,text/csv" style={{ display: "none" }} onChange={e => { read(e.target.files && e.target.files[0]); e.target.value = ""; }} />
        </label>
        <button className="btn btn-ghost btn-sm" onClick={() => downloadCsv(kind === "bills" ? "fenceworks-bills-template.csv" : "fenceworks-recurring-template.csv", toCsv(t.cols, t.rows))}><Icon name="download" size={13} />Template</button>
      </div>
      <p className="faint" style={{ fontSize: 11.5, margin: "0 0 10px" }}>
        {kind === "bills"
          ? "Any AP or open-bills export works: we look for a vendor / payee column, an amount / open balance column and a due date."
          : "Columns we look for: name (or description), amount, cadence (weekly, every 2 weeks, twice a month, monthly, quarterly, yearly — blank means monthly), next date, category."}
      </p>
      {preview && preview.error && <p style={{ color: "var(--red)", fontSize: 12.5, margin: 0 }}>{preview.error}</p>}
      {preview && !preview.error && (
        <div className="card pad" style={{ padding: 12, background: "var(--surface-2)" }}>
          <div className="row between" style={{ flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 12.5 }}><b>{fileName}</b> · {preview.rows.length} row{preview.rows.length === 1 ? "" : "s"} · <span style={{ color: fresh.length ? "var(--green)" : "var(--fg-3)" }}>{fresh.length} new</span>
              {good.length > fresh.length && <span className="muted"> · {good.length - fresh.length} already here</span>}
              {preview.rows.length > good.length && <span style={{ color: "var(--amber)" }}> · {preview.rows.length - good.length} with problems (skipped)</span>}</span>
            <div className="row gap-8">
              <button className="btn btn-ghost btn-sm" onClick={() => { setPreview(null); setFileName(""); }}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={!fresh.length} onClick={addAll}><Icon name="plus" size={13} />Add {fresh.length}</button>
            </div>
          </div>
          {preview.missing.length > 0 && <p style={{ color: "var(--amber)", fontSize: 12.5, margin: "0 0 8px" }}>Couldn't find a column for {preview.missing.join(", ")}. Rename the header (or use the template) and upload again.</p>}
          <div style={{ overflowX: "auto", maxHeight: 280, overflowY: "auto" }}>
            <table className="rep-src-table" style={{ minWidth: 480 }}>
              <thead><tr><th>Line</th><th>{kind === "bills" ? "Vendor" : "Name"}</th><th className="num">Amount</th><th>{kind === "bills" ? "Due" : "Next · cadence"}</th><th>Status</th></tr></thead>
              <tbody>
                {preview.rows.slice(0, 200).map((r: any) => (
                  <tr key={r.line}>
                    <td className="faint tnum">{r.line}</td>
                    <td style={{ fontWeight: 600 }}>{r.data.vendor || r.data.name || "—"}</td>
                    <td className="num tnum">{r.data.amount != null ? CS_money2(r.data.amount) : "—"}</td>
                    <td className="tnum">{(kind === "bills" ? r.data.due : r.data.next) || "—"}{kind !== "bills" ? " · " + ((CADENCES.find((c) => c.id === r.data.cadence) || {}) as any).label : ""}</td>
                    <td style={{ color: r.ok ? "var(--green)" : "var(--amber)", fontSize: 12 }}>{r.ok ? (have.has(sig(r.data)) ? "already here" : "✓ ready") : r.errors.join(" · ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- QuickBooks: bank balance + open bills (read-only) ----
   Only offered when QuickBooks is connected AND the service is reachable —
   the sandbox and local mode have no service, so they get the quiet link. */
function QboStrip({ store, cash, setC, go }: any) {
  const DB: any = window.VFP_DB;
  const conn = (store.settings && store.settings.integrations && store.settings.integrations.quickbooks) || {};
  const live = !!(conn.connected && DB && DB.qboCash && DB.apiBase && DB.apiBase());
  const [busy, setBusy] = React.useState<any>("");
  const [err, setErr] = React.useState<any>("");
  if (!live) return (
    <p className="faint" style={{ fontSize: 11.5, margin: 0 }}>
      On QuickBooks? <a style={{ color: "var(--teal)", cursor: "pointer" }} onClick={() => go && go("integrations")}>Connect it</a> and this pulls your bank balance and open bills in one tap.
    </p>
  );
  const run = (what: string) => {
    setBusy(what); setErr("");
    DB.qboCash().then((r: any) => {
      if (!r || r.connected === false) { setErr("QuickBooks isn't connected any more — reconnect it in Integrations."); return; }
      if (what === "balance") {
        if (!(r.bankAccounts && r.bankAccounts.length)) { setErr("No active bank accounts in QuickBooks."); return; }
        setC({ startBalance: Math.round(r.bankBalance * 100) / 100, startAt: Date.now(), balanceSource: "quickbooks", qboAsOf: r.asOf || null, qboAccounts: r.bankAccounts });
        notify(`Cash today set from QuickBooks: ${CS_money0(r.bankBalance)} across ${r.bankAccounts.length} bank account${r.bankAccounts.length === 1 ? "" : "s"}`);
      } else {
        const m = mergeQboBills(cash.bills || [], r.openBills || [], Date.now());
        setC({ bills: m.bills });
        notify(m.added || m.updated || m.closed
          ? `QuickBooks bills: ${m.added} new, ${m.updated} updated${m.closed ? `, ${m.closed} marked paid (no longer open in QuickBooks)` : ""}`
          : "QuickBooks bills are already up to date");
      }
    }).catch((e: any) => setErr((e && e.message) || "QuickBooks didn't answer — try again in a minute."))
      .finally(() => setBusy(""));
  };
  return (
    <div className="row gap-8" style={{ flexWrap: "wrap" }}>
      <button className="btn btn-outline btn-sm" disabled={!!busy} onClick={() => run("balance")}><Icon name="dollar" size={13} />{busy === "balance" ? "Asking QuickBooks…" : "Use QuickBooks bank balance"}</button>
      <button className="btn btn-outline btn-sm" disabled={!!busy} onClick={() => run("bills")}><Icon name="clipboard" size={13} />{busy === "bills" ? "Asking QuickBooks…" : "Import open bills from QuickBooks"}</button>
      {err && <span style={{ color: "var(--amber)", fontSize: 12 }}>{err}</span>}
    </div>
  );
}

function CashSchedules({ cash, setC }: any) {
  const [tab, setTab] = React.useState<any>("bills");
  const n = (k: string) => (cash[k] || []).filter((r: any) => !r.paid).length;
  const opts = [
    { id: "bills", label: "Bills" + (n("bills") ? ` · ${n("bills")}` : "") },
    { id: "recurring", label: "Recurring" + (n("recurring") ? ` · ${n("recurring")}` : "") },
    { id: "payroll", label: "Payroll" + (n("payroll") ? ` · ${n("payroll")}` : "") },
    { id: "debt", label: "Loans" + (n("debt") ? ` · ${n("debt")}` : "") },
    { id: "upload", label: "Upload CSV" },
  ];
  return (
    <div>
      <Seg label="Money-out schedules" value={tab} onChange={setTab} options={opts} />
      <div style={{ marginTop: 12 }}>
        {tab === "upload" ? <CsvUpload cash={cash} setC={setC} /> : <ScheduleList kind={tab} rows={cash[tab] || []} onChange={(next: any[]) => setC({ [tab]: next })} />}
      </div>
    </div>
  );
}

export { CashSchedules, QboStrip, KINDS as CASH_KINDS };
